import _ from 'lodash';
import React, {FunctionComponent, ReactNode} from 'react';
import {Grid, Tooltip} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import {
    ProductFieldToolTip,
    ProductFieldAttributesToDisplayNameMap,
} from '@treez/commons/utils/product';
import {Title, TitleSize} from '@treez-ui/core/layout/Title';
import {Button, ButtonClass} from '@treez-ui/core/buttons';
import {
    ProductAttributesField,
    ProductField,
    ProductData,
} from '@treez/commons/sharedTypings/product';
import {Checkbox} from '@treez-ui/core/forms';
import {useHistory, useLocation} from 'react-router-dom';
import {TreezColor, treezColorMap} from '~/styles/constants';
import {MergeProductParsedQs} from '../ProductMerge';
import {generateQueryString, parseQueryString} from '~/lib/routing';

interface Props {
    customHeaderCell?: ReactNode;
    fieldsToShow: ProductData[];
    hideExactMatch?: boolean;
    resetMergeProduct: () => void;
}

export const FieldTitleColumn: FunctionComponent<Props> = (props: Props) => {
    const {customHeaderCell, fieldsToShow, hideExactMatch, resetMergeProduct} = props;

    const location = useLocation();
    const history = useHistory();

    // Component Styles
    const titleContainer = {
        alignItems: 'center',
        display: 'flex',
        fontSize: '14px',
        fontWeight: 600,
        paddingLeft: '4px',
    };

    const {GREY_MEDIUM, OFF_WHITE} = TreezColor;

    const useStyles = makeStyles({
        actionButton: {
            margin: '12px',
            width: '120px',
        },
        actionButtons: {
            margin: '0 auto',
        },
        actionButtonContainer: {
            ...titleContainer,
            height: '183px',
            paddingBottom: '10px',
            alignItems: 'unset',
        },
        descriptionsContainer: {
            height: '400px',
            ...titleContainer,
        },
        multiValueContainer: {
            height: '200px',
            ...titleContainer,
        },
        overViewTitle: {
            height: '131px',
            backgroundColor: treezColorMap[OFF_WHITE],
            borderBottom: `1px solid ${treezColorMap[GREY_MEDIUM]}`,
            ...titleContainer,
        },
        selectAllContainer: {
            height: '34px',
            border: '1px solid transparent',
        },
        singleValue: {
            height: '80px',
            ...titleContainer,
        },
        storePresenceTitle: {
            ...titleContainer,
            height: '100px',
        },
        titleColumn: {
            minWidth: '125px',
            flexWrap: 'nowrap',
        },
    });

    const {
        actionButton,
        actionButtons,
        actionButtonContainer,
        descriptionsContainer,
        multiValueContainer,
        overViewTitle,
        selectAllContainer,
        singleValue,
        storePresenceTitle,
        titleColumn,
    } = useStyles();

    const handleToggleHideMatches = (value: boolean) => {
        const queryParams = parseQueryString(location.search) as MergeProductParsedQs;

        const newParams = {...queryParams, hideExactMatch: value};

        const queryString = generateQueryString(newParams);

        history.push({
            search: queryString,
            state: location.state,
        });
    };

    const tooltipPlacement = 'top-start';

    const renderSingleValue = (field, displayName, idx) => {
        const tooltip = ProductFieldToolTip[field];

        if (tooltip != null && tooltip != '') {
            return (
                <Tooltip
                    key={`${field}-${idx}`}
                    placement={tooltipPlacement}
                    title={<div className="tooltip">{tooltip}</div>}
                >
                    <div className={singleValue}>{displayName}</div>
                </Tooltip>
            );
        }
        return (
            <Grid item className={singleValue} key={`${field}-${idx}`}>
                {displayName}
            </Grid>
        );
    };

    return (
        <Grid item container direction="column" className={titleColumn}>
            <Grid className={storePresenceTitle}>Store Presence</Grid>
            <Grid item>
                <div className={selectAllContainer} />
            </Grid>
            <Grid item className={actionButtonContainer}>
                <div className={actionButtons}>
                    <Button
                        buttonClass={ButtonClass.QUIET}
                        onClick={resetMergeProduct}
                        className={actionButton}
                        text="Reset All"
                    />
                    <Checkbox
                        checked={hideExactMatch}
                        label="Hide Exact Matches"
                        onChange={() => handleToggleHideMatches(!hideExactMatch)}
                    />
                </div>
            </Grid>
            {customHeaderCell || (
                <Grid item className={overViewTitle}>
                    <Title size={TitleSize.SMALL} text="Overview" />
                </Grid>
            )}
            {_.map(fieldsToShow, (field, idx) => {
                const displayName = ProductFieldAttributesToDisplayNameMap[field];

                if (_.includes(ProductAttributesField, field)) {
                    return (
                        <Grid className={multiValueContainer} item key={`${field}-${idx}`}>
                            {displayName}
                        </Grid>
                    );
                }
                if (field === ProductField.DESCRIPTIONS) {
                    return (
                        <Tooltip
                            key={`${field}-${idx}`}
                            placement={tooltipPlacement}
                            title={
                                <Title
                                    size={TitleSize.SMALL}
                                    text={ProductFieldToolTip[field] || ''}
                                />
                            }
                        >
                            <div className={descriptionsContainer}>{displayName}</div>
                        </Tooltip>
                    );
                }
                return renderSingleValue(field, displayName, idx);
            })}
        </Grid>
    );
};

export default FieldTitleColumn;
